import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { ApiEndpoints, Constants } from '../../constants/appConstants';
import httpService from '../../services/httpService';
import { HubConnectionBuilder } from '@microsoft/signalr/dist/esm/HubConnectionBuilder';
import { HttpTransportType, LogLevel } from '@microsoft/signalr';

const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];

type InitiateMultipartResponse = {
  uploadId: string;
  fileKey: string;
  resourceId?: string;
  isGuardDutyEnabled?: boolean;
};

const HelpDocumentsSection = () => {
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [canUpload, setCanUpload] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<string[]>([]);
  const [connection, setConnection] = useState<any>(null);
  const [scanInfo, setScanInfo] = useState<any[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [pendingCompleteRequests, setPendingCompleteRequests] =
    useState<Map<string, () => Promise<void>>>(new Map());

  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);

  const showNotification = (
    severity: 'success' | 'error' | 'warn',
    detail: string
  ) => {
    toast.current?.show({
      severity,
      summary:
        severity === 'success'
          ? 'Success'
          : severity === 'warn'
            ? 'Warning'
            : 'Error',
      detail,
      life: 3000,
    });
  };

  useEffect(() => {
    fetchDocumentTypes();
  }, []);

  useEffect(() => {
    const newConnection = new HubConnectionBuilder()
      .withUrl(ApiEndpoints.fileProcessingHub, {
        transport: HttpTransportType.LongPolling,
        accessTokenFactory: () => localStorage.getItem('authToken') ?? '',
      })
      .configureLogging(LogLevel.Information)
      .build();

    newConnection
      .start()
      .then(() => {
        setConnection(newConnection);
      })
      .catch((error) => {
        console.error(
          'Error establishing SignalR connection:',
          error
        );
      });

    return () => {
      newConnection.stop().catch((error) => {
        console.error(
          'Error stopping SignalR connection:',
          error
        );
      });
    };
  }, []);

  useEffect(() => {
    if (!connection) {
      return;
    }

    const handleScanInfo = (message: any) => {
      setScanInfo((previousScanInfo) => [
        ...previousScanInfo,
        message,
      ]);
    };

    connection.on('ScanInfo', handleScanInfo);

    return () => {
      connection.off('ScanInfo', handleScanInfo);
    };
  }, [connection]);

  useEffect(() => {
    if (
      !isScanning ||
      pendingCompleteRequests.size === 0 ||
      scanInfo.length === 0
    ) {
      return;
    }

    const scannedFiles = scanInfo
      .filter(
        (item) =>
          item.fileName &&
          item.isFine === true
      )
      .map((item) => item.fileName);

    const scanFailedFiles = scanInfo
      .filter(
        (item) =>
          item.fileName &&
          item.isFine === false
      )
      .map((item) => item.fileName);

    scanFailedFiles.forEach((fileName: string) => {
      if (pendingCompleteRequests.has(fileName)) {
        showNotification(
          'error',
          `File ${fileName} failed security scan.`
        );

        setIsScanning(false);
        setIsLoading(false);
        setCanUpload(false);
        setUploadProgress(0);
      }
    });

    scannedFiles.forEach((fileName: string) => {
      const completeRequest =
        pendingCompleteRequests.get(fileName);

      if (completeRequest) {
        completeRequest()
          .catch((error) => {
            const errorMessage =
              error?.response?.data?.message ||
              error?.message ||
              'Failed to complete document upload. Please try again.';

            showNotification(
              'error',
              errorMessage
            );

            setCanUpload(false);

            console.error(
              'Error completing resource upload after security scan:',
              error
            );
          })
          .finally(() => {
            setIsScanning(false);
            setIsLoading(false);
          });
      }
    });

    if (
      scannedFiles.length > 0 ||
      scanFailedFiles.length > 0
    ) {
      setPendingCompleteRequests(
        (previousRequests) => {
          const newRequests = new Map(
            previousRequests
          );

          [
            ...scannedFiles,
            ...scanFailedFiles,
          ].forEach((fileName: string) => {
            newRequests.delete(fileName);
          });

          return newRequests;
        }
      );
    }
  }, [
    scanInfo,
    pendingCompleteRequests,
    isScanning,
  ]);

  const fetchDocumentTypes = async () => {
    try {
      const response =
        await httpService.get<string[]>(
          `${ApiEndpoints.BASE_URL}/Resource/GetResourceDocumentTypes`
        );

      setDocumentTypes(response.data);
    } catch (error: any) {
      showNotification(
        'error',
        `Error fetching document types: ${error.message}`
      );
    }
  };

  const fileFormatHint = useMemo(
    () =>
      `Accepted formats: ${allowedExtensions
        .map((extension) => `.${extension}`)
        .join(', ')}`,
    []
  );

  const validateFile = (file: File) => {
    const extension =
      file.name
        .split('.')
        .pop()
        ?.toLowerCase() ?? '';

    return allowedExtensions.includes(extension);
  };

  const handleUpload = async (
    event: FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    if (!selectedDocumentType || !selectedFile) {
      showNotification(
        'warn',
        'Please select both a document type and a file before uploading.'
      );

      return;
    }

    const file = selectedFile;

    if (!validateFile(file)) {
      showNotification(
        'warn',
        'Selected file format is not supported. Please upload a supported file type.'
      );

      return;
    }

    if (file.size > 1024 * 1024 * 1024) {
      showNotification(
        'warn',
        'File size should be less than 1 GB.'
      );

      setCanUpload(false);
      return;
    }

    setIsLoading(true);
    setCanUpload(true);
    setUploadProgress(0);
    setScanInfo([]);

    try {
      const chunkSize = 5 * 1024 * 1024;
      const totalChunks = Math.ceil(
        file.size / chunkSize
      );

      const tags: string[] = [];

      const initiateResult =
        await httpService.post<InitiateMultipartResponse>(
          ApiEndpoints.initiateResourceMultipartUploadUrl,
          {
            documentType: selectedDocumentType,
            fileName: file.name,
            connectionId: connection?.connectionId,
            uploadType:
              Constants.UPLOAD_TYPE_RESOURCE,
          }
        );

      const uploadId =
        initiateResult.data.uploadId;

      const fileKey =
        initiateResult.data.fileKey;

      const resourceId =
        initiateResult.data.resourceId;

      for (
        let i = 0;
        i < totalChunks;
        i += 1
      ) {
        const start = i * chunkSize;
        const end = Math.min(
          start + chunkSize,
          file.size
        );

        const chunk = file.slice(
          start,
          end
        );

        const formData = new FormData();

        formData.append(
          'file',
          chunk,
          file.name
        );

        formData.append(
          'chunkIndex',
          i.toString()
        );

        formData.append(
          'uploadId',
          uploadId
        );

        formData.append(
          'fileKey',
          fileKey
        );

        formData.append(
          'isLastChunk',
          (i === totalChunks - 1).toString()
        );

        formData.append(
          'uploadType',
          Constants.UPLOAD_TYPE_RESOURCE
        );

        const chunkResult =
          await httpService.post<{
            tag?: string;
          }>(
            ApiEndpoints.uploadResourceMultipart,
            formData
          );

        if (chunkResult.data?.tag) {
          tags.push(chunkResult.data.tag);
        }

        const percentComplete =
          Math.round(
            ((i + 1) / totalChunks) * 100
          );

        setUploadProgress(
          percentComplete
        );
      }

      const completeUpload =
        async () => {
          await httpService.post(
            ApiEndpoints.completeResourceMultipartUploadUrl,
            {
              uploadId,
              fileKey,
              tags,
              resourceId,
              uploadType:
                Constants.UPLOAD_TYPE_RESOURCE,
            }
          );

          showNotification(
            'success',
            'Document uploaded successfully.'
          );

          resetUploadState();
        };

      if (
        initiateResult.data
          .isGuardDutyEnabled
      ) {
        setIsScanning(true);

        setPendingCompleteRequests(
          (previousRequests) => {
            const newRequests = new Map(
              previousRequests
            );

            newRequests.set(
              file.name,
              completeUpload
            );

            return newRequests;
          }
        );

        return;
      }

      await completeUpload();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to upload document. Please try again.';

      showNotification(
        'error',
        errorMessage
      );

      setCanUpload(false);

      console.error(
        'Error uploading help document:',
        error
      );
    } finally {
      if (!isScanning) {
        setIsLoading(false);
      }
    }
  };

  const resetUploadState = () => {
    setSelectedDocumentType('');
    setSelectedFile(null);
    setUploadProgress(0);
    setCanUpload(false);
    setIsScanning(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <Toast
        ref={toast}
        position='top-right'
        appendTo={document.body}
      />

      <p className='admin-section-description'>
        Upload help documents by selecting a document type and choosing a file.
      </p>

      <form
        className='admin-help-grid'
        onSubmit={handleUpload}
      >
        <div className='admin-field'>
          <label htmlFor='documentType'>
            Document Type *
          </label>

          <select
            id='documentType'
            value={selectedDocumentType}
            onChange={(event) => {
              setSelectedDocumentType(
                event.target.value
              );

              setSelectedFile(null);
              setUploadProgress(0);

              if (fileInputRef.current) {
                fileInputRef.current.value =
                  '';
              }
            }}
            disabled={isLoading}
          >
            <option value=''>
              Select document type
            </option>

            {documentTypes.map(
              (option) => (
                <option
                  key={option}
                  value={option}
                >
                  {option}
                </option>
              )
            )}
          </select>
        </div>

        <div className='admin-field'>
          <label htmlFor='helpDocument'>
            Select files to upload *
          </label>

          <input
            id='helpDocument'
            type='file'
            title={fileFormatHint}
            ref={fileInputRef}
            disabled={isLoading}
            onChange={(event) => {
              const file =
                event.target.files?.[0] ??
                null;

              setSelectedFile(file);
              setUploadProgress(0);
            }}
          />
        </div>

        <div className='admin-upload-actions'>
          <button
            className='admin-btn admin-btn-primary'
            type='submit'
            disabled={isLoading}
          >
            {isScanning
              ? 'Scanning...'
              : isLoading
                ? 'Uploading...'
                : 'Upload'}
          </button>
        </div>

        {canUpload && (
          <div className='admin-progress-row'>
            <span>
              {isScanning
                ? 'Scanning file for security threats...'
                : `${uploadProgress}% Uploaded...`}
            </span>

            <ProgressBar
              value={uploadProgress}
              showValue={false}
            />
          </div>
        )}
      </form>
    </>
  );
};

export default HelpDocumentsSection;